/* ---------------------------------------
    IMPORTS & CONSTANTS.
--------------------------------------- */
import React, { useEffect, useRef, useState } from "react";
import { BrowserRouter as Router, Routes, Route, Link } from "react-router-dom";
import WhatsNew from "./pages/whatsnew";
import TopLosers from "./pages/TopLosers";
import VolumeAnomalies from "./pages/VolumeAnomalies";
import TopMovers from "./pages/TopMovers";
import "./App.css";
import SearchBar from "./components/SearchBar/SearchBar";
import { CoinlistProvider } from "./context/CoinlistContext";

const API_URL = process.env.REACT_APP_getcoinstopapi;
const REFRESH_INTERVAL = process.env.NODE_ENV === "development" ? 30 : 60;

/* ---------------------------------------
   HELPERS
--------------------------------------- */


/* =====================================================
   APP COMPONENT
===================================================== */
function App() {
  /* ---------------- STATE ---------------- */
  const [maintenance, setMaintenance] = useState(null);
  const [data, setData] = useState({
    top5m: [], top15m: [], top1h: [], top24h: [],
    losers5m: [], losers15m: [], losers1h: [], losers24h: [],
    volumeAnomalies: []
  });
  const [lastFetched, setLastFetched] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const [rows5m, setRows5m] = useState(Number(localStorage.getItem("rows5m")) || 10);
  const [rows15m, setRows15m] = useState(Number(localStorage.getItem("rows15m")) || 10);
  const [rows1h, setRows1h] = useState(Number(localStorage.getItem("rows1h")) || 10);
  const [rows24h, setRows24h] = useState(Number(localStorage.getItem("rows24h")) || 10);

  const [theme, setTheme] = useState(localStorage.getItem("theme") || "dark");
  const [paused, setPaused] = useState(false);
  const [remaining, setRemaining] = useState(REFRESH_INTERVAL);
  const secTimer = useRef(null);

  const [selectedExchanges, setSelectedExchanges] = useState(() => {
  const saved = localStorage.getItem("selectedExchanges");
  return saved ? JSON.parse(saved) : ["MEXC", "HyperLiquid", "Bitunix", "Bybit"];
});
  useEffect(() => {
  localStorage.setItem("selectedExchanges", JSON.stringify(selectedExchanges));
}, [selectedExchanges]);

  const toggleExchange = (ex) => {
    setSelectedExchanges((prev) =>
      prev.includes(ex) ? prev.filter((e) => e !== ex) : [...prev, ex]
    );
  };

  const [sidebarOpen, setSidebarOpen] = useState(false); // sidebar state

  // Load coinlist once on app startup
const [coinlist, setCoinlist] = useState([]);
console.log("coinlist loaded:", coinlist);
useEffect(() => {
  async function loadCoinlist() {
    try {
      const res = await fetch(process.env.REACT_APP_COINLISTGETAPI
);
      const data = await res.json();
      setCoinlist(data);
    } catch (err) {
      console.error("Failed to load coinlist:", err);
    }
  }

  loadCoinlist();
}, []);

  /* ---------------- EFFECTS ---------------- */
  useEffect(() => localStorage.setItem("rows5m", rows5m), [rows5m]);
  useEffect(() => localStorage.setItem("rows15m", rows15m), [rows15m]);
  useEffect(() => localStorage.setItem("rows1h", rows1h), [rows1h]);
  useEffect(() => localStorage.setItem("rows24h", rows24h), [rows24h]);

  useEffect(() => {
    document.body.setAttribute("data-theme", theme);
    localStorage.setItem("theme", theme);
  }, [theme]);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch("/maintenance.json", { cache: "no-store" });
        if (!res.ok) throw new Error();
        const json = await res.json();
        setMaintenance(json);
      } catch {
        setMaintenance({ enabled: false });
      }
    })();
  }, []);

  const fetchData = async () => {
    try {
      const res = await fetch(API_URL);
      if (!res.ok) throw new Error();
      const json = await res.json();

      setData({
        top5m: json.gainers?.["5m"] ?? [],
        top15m: json.gainers?.["15m"] ?? [],
        top1h: json.gainers?.["1h"] ?? [],
        top24h: json.gainers?.["24h"] ?? [],
        losers5m: json.losers?.["5m"] ?? [],
        losers15m: json.losers?.["15m"] ?? [],
        losers1h: json.losers?.["1h"] ?? [],
        losers24h: json.losers?.["24h"] ?? [],
        volumeAnomalies: json.volumeAnomalies ?? []
      });

      if (json.computedAt) setLastFetched(json.computedAt);
      setError(false);
    } catch {
      setError(true);
    }
  };

  const forceRefresh = async () => {
    clearInterval(secTimer.current);
    setLoading(true);
    await fetchData();
    setLoading(false);
    setRemaining(REFRESH_INTERVAL);
    if (!paused) {
      secTimer.current = setInterval(() => setRemaining((r) => (r > 0 ? r - 1 : 0)), 1000);
    }
  };

  useEffect(() => { fetchData().then(() => setLoading(false)); }, []);
  useEffect(() => {
    if (paused) return;
    secTimer.current = setInterval(() => setRemaining((r) => (r > 0 ? r - 1 : 0)), 1000);
    return () => clearInterval(secTimer.current);
  }, [paused]);
  useEffect(() => {
    if (remaining === 0 && !paused) { fetchData(); setRemaining(REFRESH_INTERVAL); }
  }, [remaining, paused]);

  const progress = (remaining / REFRESH_INTERVAL) * 100;

  /* ---------------- EARLY RETURNS ---------------- */
  if (maintenance === null) return <p className="loading">Loading…</p>;
  if (maintenance.enabled)
    return (
      <div className="maintenance-page">
        <h1>🛠️ Under Maintenance</h1>
        <p>{maintenance.message}</p>
      </div>
    );

  /* ---------------- FILTERED DATA ---------------- */
  const filteredTop5m = (data.top5m ?? []).filter(c => c.exchanges?.some(e => selectedExchanges.includes(e))).sort((a,b)=>b.change-a.change);
  const filteredTop15m = (data.top15m ?? []).filter(c => c.exchanges?.some(e => selectedExchanges.includes(e))).sort((a,b)=>b.change-a.change);
  const filteredTop1h = (data.top1h ?? []).filter(c => c.exchanges?.some(e => selectedExchanges.includes(e))).sort((a,b)=>b.change-a.change);
  const filteredTop24h = (data.top24h ?? []).filter(c => c.exchanges?.some(e => selectedExchanges.includes(e))).sort((a,b)=>b.change-a.change);

  const filteredLosers5m = (data.losers5m ?? []).filter(c => c.exchanges?.some(e => selectedExchanges.includes(e))).sort((a,b)=>a.change-b.change);
  const filteredLosers15m = (data.losers15m ?? []).filter(c => c.exchanges?.some(e => selectedExchanges.includes(e))).sort((a,b)=>a.change-b.change);
  const filteredLosers1h = (data.losers1h ?? []).filter(c => c.exchanges?.some(e => selectedExchanges.includes(e))).sort((a,b)=>a.change-b.change);
  const filteredLosers24h = (data.losers24h ?? []).filter(c => c.exchanges?.some(e => selectedExchanges.includes(e))).sort((a,b)=>a.change-b.change);

  /* ---------------- NORMAL APP ---------------- */
  return (
   <CoinlistProvider>
    <Router>
      <div className={`refresh-bar ${paused ? "paused" : ""}`}>
        <div className="refresh-progress" style={{ width: `${progress}%` }} />
      </div>

      <div className="App">
{/* TOP BAR */}
<header className="header">
  <div className="header-top-bar">

    {/* LEFT SECTION */}
    <div className="header-left">
      <button className="burger-btn" onClick={() => setSidebarOpen(prev => !prev)}>☰</button>
      <h1 className="site-title">Crypto Movers</h1>
    </div>

    {/* CENTER SECTION — SEARCH BAR MOVED HERE */}
    <div className="header-center">
      <SearchBar />
    </div>

    {/* RIGHT SECTION (empty for now) */}
    <div className="header-right"></div>

  </div>

  {process.env.NODE_ENV === "development" && lastFetched && (
    <div className="last-fetched">
      Data updated at: {new Date(lastFetched).toLocaleString()}
    </div>
  )}
</header>

{/* SIDEBAR */}
<aside className={`sidebar ${sidebarOpen ? "open" : ""}`}>
  {/* Close button */}
  <button className="close-btn" onClick={() => setSidebarOpen(false)}>×</button>

  <nav className="sidebar-nav">
    <Link to="/" onClick={() => setSidebarOpen(false)}>Top Movers</Link>
    <Link to="/losers" onClick={() => setSidebarOpen(false)}>Top Losers</Link>
    <Link to="/volume" onClick={() => setSidebarOpen(false)}>Volume Analyser</Link>
    <Link to="/whatsnew" onClick={() => setSidebarOpen(false)}>What’s New</Link>
  </nav>

  <div className="sidebar-controls">
    {/* Pause Auto-Refresh Toggle */}
    <label className="switch">
      <input 
        type="checkbox" 
        checked={!paused} 
        onChange={() => setPaused(prev => !prev)} 
      />
      <span className="slider"></span>
      <span className="switch-label">Auto-Refresh</span>
    </label>

  {/* Theme Toggle */}
  <label className="switch">
    <input 
      type="checkbox" 
      checked={theme === "dark"} 
      onChange={() => setTheme(prev => prev === "dark" ? "light" : "dark")}
    />
    <span className="slider"></span>
    <span className="switch-label">Dark Mode</span>
  </label>
</div>
</aside>

        {/* ROUTES */}
        <main className="main-content">
          <Routes>
            <Route path="/" element={
              !loading && !error ? (
                <TopMovers
                  data={{ top5m: filteredTop5m, top15m: filteredTop15m, top1h: filteredTop1h, top24h: filteredTop24h }}
                  rows5m={rows5m} rows15m={rows15m} rows1h={rows1h} rows24h={rows24h}
                  setRows5m={setRows5m} setRows15m={setRows15m} setRows1h={setRows1h} setRows24h={setRows24h}
                  selectedExchanges={selectedExchanges} toggleExchange={toggleExchange}
                  forceRefresh={forceRefresh}  
                  loading={loading}  
                />
              ) : loading ? <p className="loading">Loading data…</p> : <p className="error">Unable to load data</p>
            }/>
            <Route path="/whatsnew" element={<WhatsNew />} />
            <Route path="/losers" element={!loading && !error ? (
              <TopLosers data={{
                losers5m: filteredLosers5m, losers15m: filteredLosers15m,
                losers1h: filteredLosers1h, losers24h: filteredLosers24h
              }} />
            ) : <p className="loading">Loading data…</p>} />
            <Route path="/volume" element={!loading && !error ? (
              <VolumeAnomalies rows={data.volumeAnomalies} />
            ) : <p className="loading">Loading data…</p>} />
          </Routes>
        </main>
        <footer className="site-footer"> <p> Crypto Movers provides market data for informational purposes only. Prices and rankings may be delayed or inaccurate. Nothing on this site constitutes financial advice. </p> </footer>
      </div>
    </Router>
   </CoinlistProvider> 
  );
}

export default App;
